import React, { useCallback, useRef } from 'react';
import { StyleSheet, TouchableOpacity, View, type ViewInstance } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { useHovered } from '@garnish/core';
import { useResponsive } from '@garnish/core';
import { webOnlyStyles } from '@garnish/core';
import { Icon } from '@garnish/core';
import { BodyText } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardPortionStepper = (props: Props) => {
  const {
    options,
    currentPortions,
    accessibilityLabel,
    removeButtonAccessibilityLabel,
    onSelectPortion,
    onRemove,
  } = props;

  const { match } = useResponsive();

  const containerStyles = [styles.container, match([styles.containerXS, styles.containerSM])];

  return (
    <Animated.View
      entering={FadeIn.duration(theme.transitions.short)}
      exiting={FadeOut.duration(theme.transitions.short)}
      style={styles.outerContainer}
      pointerEvents="box-none"
    >
      <View
        style={containerStyles}
        accessibilityRole="menu"
        accessibilityLabel={accessibilityLabel}
      >
        {options.map((option) => (
          <PortionOption
            key={option.portions}
            option={option}
            isSelected={currentPortions === option.portions}
            onPress={onSelectPortion}
          />
        ))}

        {currentPortions > 0 ? (
          <TouchableOpacity
            style={[styles.removeButton, match([styles.removeButtonXS, styles.removeButtonSM])]}
            onPress={onRemove}
            accessibilityLabel={removeButtonAccessibilityLabel}
            accessibilityRole="button"
            activeOpacity={0.7}
          >
            <Icon name="IconTrash" color={theme.colors.DARK_KALE} />
          </TouchableOpacity>
        ) : null}
      </View>
    </Animated.View>
  );
};

// ─── Subcomponents ──────────────────────────────────────────────────────────

const PortionOption = (params: PortionOptionParams) => {
  const { option, isSelected, onPress } = params;

  const { match } = useResponsive();

  // ─── Refs ────────────────────────────────────────────────────────────

  const pressableRef = useRef<ViewInstance>(null);
  const isHovered = useHovered(pressableRef);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    onPress(option.portions);
  }, [option.portions, onPress]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [optionContainerWebStyles, isHovered ? styles.optionContainerHover : undefined]; // prettier-ignore
  const buttonStyle = [
    styles.optionButton,
    match([styles.optionButtonXS, styles.optionButtonSM]),
    isSelected ? styles.optionButtonSelected : undefined,
  ];
  const labelStyle = [styles.optionLabel, isSelected ? styles.optionLabelSelected : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <TouchableOpacity
        ref={pressableRef}
        style={buttonStyle}
        onPress={handlePress}
        accessibilityLabel={option.label}
        accessibilityRole="menuitem"
        aria-selected={isSelected}
        activeOpacity={0.7}
      >
        <BodyText sizeMatch={['14', '16']} style={labelStyle}>
          {option.label}
        </BodyText>

        {option.priceLabel ? (
          <BodyText sizeMatch={['14', '16']} style={labelStyle}>
            {option.priceLabel}
          </BodyText>
        ) : null}
      </TouchableOpacity>
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.colors.CREAM,
    gap: theme.spacing['2'],
    boxShadow: '0px 2px 30px 0px rgba(14, 21, 14, 0.24)',
    paddingTop: theme.spacing['1'],
    paddingHorizontal: theme.spacing['1'],
    paddingBottom: theme.spacing['2'],
  },
  containerXS: {
    borderRadius: 26,
  },
  containerSM: {
    borderRadius: 30,
  },
  optionContainerHover: {
    opacity: 0.8,
  },
  optionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['3'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  optionButtonXS: {
    minWidth: 80,
    minHeight: 48,
    paddingVertical: theme.spacing['2'],
  },
  optionButtonSM: {
    minWidth: 108,
    minHeight: 56,
    paddingVertical: theme.spacing['1'],
  },
  optionButtonSelected: {
    backgroundColor: theme.colors.SPINACH,
  },
  optionLabel: {
    color: theme.colors.SPINACH,
  },
  optionLabelSelected: {
    color: theme.colors.WHITE,
  },
  removeButton: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  removeButtonXS: {
    paddingHorizontal: theme.spacing['4'],
  },
  removeButtonSM: {
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['6'],
  },
});

const optionContainerWebStyles = webOnlyStyles({
  transition: `opacity ${theme.transitions.short}ms`,
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  options: PortionOptionConfig[];
  currentPortions: number;
  accessibilityLabel?: string;
  removeButtonAccessibilityLabel?: string;
  onSelectPortion: (portions: number) => void;
  onRemove: () => void;
};

type PortionOptionConfig = {
  label: string;
  portions: number;
  priceLabel?: string;
};

type PortionOptionParams = {
  option: PortionOptionConfig;
  isSelected: boolean;
  onPress: (portions: number) => void;
};

export type { PortionOptionConfig };
